import { useCallback, useEffect, useState } from "react";
import { Box, Text, useBreakpointValue } from "@chakra-ui/react";
import Sk from "skulpt";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";

import { python } from "@codemirror/lang-python";
import React from "react";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";
import { EditorState } from "@codemirror/state";
import { IHightlighType } from "./Quiz/types";
import EditorDots from "../components/EditorDots";
import EditorControls from "../components/EditorControls";
import {
  createHighlightPlugin,
  LineHighlight,
} from "../../utils/editorPlugins";
import EditorExplanation from "../components/EditorExplanation";

export type HighlightNumberType = {
  component: IConfig["component"];
  props: IConfig["props"];
  highlightLines: number[];
  lineColor?: IHightlighType;
  markStyle?: IHightlighType;
};

export type IPythonExecutionProps = {
  code: string;
  onOutput?: (ouput: string) => void;
  onRun?: ({
    code,
  }: {
    currentExplanationIndex: number;
    code: string;
    setCode: any;
    setOutput: any;
    runCode: any;
  }) => void;
  onChange?: (code: string) => void;
  hightlight?: IHightlighType;
  mode: "try" | "view" | "reveal" | "reveal-try" | "view-run";
  crowded?: boolean;
  noReset?: boolean;
  explanation?: HighlightNumberType[];
  autoHighlightChange?: boolean;
  height?: string;
  showExplanation?: boolean;
  noDots?: boolean;
  noLineNumbers?: boolean;
};

function PythonCodeEditor({
  code: initial_code,
  onOutput,
  hightlight = "neutral",
  mode = "try",
  crowded,
  noReset,
  explanation,
  autoHighlightChange,
  showExplanation = false,
  noDots = false,
  noLineNumbers = false,
  height = "2rem",
  ...props
}: IPythonExecutionProps) {
  const [code, setCode] = useState(() => initial_code);
  const [output, setOutput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);
  const [hightlightColor, setHightlightColor] = useState(() => hightlight);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const hasHighlights = explanation && explanation.length > 0;
  const currentExplanation = hasHighlights
    ? explanation[currentHighlightIndex]
    : null;

  const getcurrentExplanation = useCallback((): LineHighlight | null => {
    if (!currentExplanation?.highlightLines) return null;

    return {
      lines: currentExplanation.highlightLines,
      lineStyle: currentExplanation.lineColor
        ? currentExplanation.lineColor
        : hightlightColor,
      markStyle: currentExplanation.markStyle
        ? currentExplanation.markStyle
        : hightlightColor,
    };
  }, [currentExplanation, hightlightColor]);

  const highlightPlugin = createHighlightPlugin(getcurrentExplanation);

  useEffect(() => {
    setHightlightColor(hightlight);
  }, [hightlight]);

  useEffect(() => {
    setCode(initial_code);
  }, [initial_code, mode]);

  useEffect(() => {
    if (isCompleted && onOutput && mode !== "reveal") {
      onOutput(output);
    }
  }, [output, onOutput, isCompleted]);

  useEffect(() => {
    if (mode === "reveal" || mode === "reveal-try") {
      setOutput("");
      Sk.pre = "output";
      Sk.configure({
        output: outf,
        read: builtinRead,
      });

      (Sk.TurtleGraphics ? Sk.TurtleGraphics : {}).target = "mycanvas";
      const myPromise = Sk.misceval.asyncToPromise(() => {
        return Sk.importMainWithBody("<stdin>", false, code, true);
      });

      myPromise.then(
        function () {
          if (autoHighlightChange) {
            setHightlightColor("success");
          }
        },
        function (err: any) {
          if (autoHighlightChange) {
            setHightlightColor("error");
          }
          setOutput(err.toString());
        }
      );
    }
  }, [mode]);

  function outf(text: string) {
    setOutput((prevOutput) => {
      const newOutput = prevOutput + text;
      return newOutput;
    });
  }

  function builtinRead(x: string) {
    if (
      Sk.builtinFiles === undefined ||
      Sk.builtinFiles["files"][x] === undefined
    )
      throw `File not found: '${x}'`;
    return Sk.builtinFiles["files"][x];
  }

  const onChange = React.useCallback((val: any) => {
    if (props.onChange) {
      props.onChange(val);
    }
    setCode(val);
  }, []);

  function runCode() {
    setIsRunning(true);
    setIsCompleted(false);
    setOutput("");

    Sk.pre = "output";
    Sk.configure({
      output: outf,
      read: builtinRead,
    });

    (Sk.TurtleGraphics ? Sk.TurtleGraphics : {}).target = "mycanvas";
    const myPromise = Sk.misceval.asyncToPromise(() => {
      return Sk.importMainWithBody("<stdin>", false, code, true);
    });

    myPromise.then(
      function () {
        setIsRunning(false);
        setIsCompleted(true);
        if (autoHighlightChange) {
          setHightlightColor("success");
        }
      },
      function (err: any) {
        setOutput(err.toString());
        if (autoHighlightChange) {
          setHightlightColor("error");
        }
        setIsRunning(false);
        setIsCompleted(true);
      }
    );
  }

  const handlePrevious = () => {
    if (currentHighlightIndex > 0) {
      setCurrentHighlightIndex(currentHighlightIndex - 1);
    }
  };

  const handleNext = () => {
    if (explanation && currentHighlightIndex < explanation.length - 1) {
      setCurrentHighlightIndex(currentHighlightIndex + 1);
    }
  };

  return (
    <Box my={"1.5rem"} px={"1rem"}>
      <Box
        border={"1px solid"}
        borderColor={"neutral.900"}
        borderTopRadius={".5rem"}
        bg={"#1E1E1E"}
        p={".5rem"}
      >
        {!noDots && <EditorDots />}
        <CodeMirror
          style={{ fontSize: isMobile ? ".9rem" : "1rem", width: "100%" }}
          value={code}
          minHeight={height}
          readOnly={mode === "reveal" || mode === "view" || mode === "view-run"}
          extensions={[
            python(),
            highlightPlugin,
            EditorView.lineWrapping,
            ...(mode === "reveal" || mode === "view" || mode === "view-run"
              ? [
                  EditorState.readOnly.of(true),
                  EditorView.editable.of(false),
                  EditorView.theme({
                    ".cm-activeLine": {
                      backgroundColor: "none",
                    },
                    ".cm-cursor": {
                      display: "none",
                    },
                  }),
                ]
              : []),
          ]}
          onChange={onChange}
          theme={vscodeDark}
          basicSetup={{
            lineNumbers: noLineNumbers ? false : true,
            highlightActiveLineGutter:
              mode === "reveal" || mode === "view" || mode === "view-run"
                ? false
                : true,
            foldGutter: false,
            dropCursor: false,
            allowMultipleSelections: false,
            indentOnInput: false,
            bracketMatching: false,
            closeBrackets: false,
            autocompletion: false,
            rectangularSelection: false,
            crosshairCursor: false,
            lintKeymap: false,
            highlightActiveLine:
              mode === "reveal" || mode === "view" || mode === "view-run"
                ? false
                : true,
          }}
        />
      </Box>

      <Box
        border={"1px solid"}
        borderColor={"neutral.800"}
        borderBottomRadius={".5rem"}
        borderTopColor={"transparent"}
      >
        {(mode === "try" ||
          mode === "reveal-try" ||
          mode === "reveal" ||
          mode === "view-run") && (
          <Box
            p={"1rem"}
            pt={"0.5rem"}
            borderBottom={"1px solid "}
            border={hightlight !== "neutral" ? "1px solid" : "none"}
            borderColor={
              hightlight === "neutral"
                ? "neutral.800"
                : hightlight === "success"
                ? "green.500"
                : "yellow.600"
            }
          >
            <Text mb={".5rem"} color={"whiteAlpha.600"} fontSize={"sm"}>
              output:
            </Text>
            <Box
              as="pre"
              sx={{
                whiteSpace: "pre-wrap",
                fontsize: { base: "1rem", lg: "md" },
              }}
            >
              {output}
            </Box>
          </Box>
        )}

        {(mode === "try" || mode === "reveal-try" || mode === "view-run") && (
          <EditorControls
            code={code}
            language={"python"}
            crowded={crowded}
            noReset={noReset}
            isRunning={isRunning}
            RunOnClick={runCode}
            ResetOnClick={() => {
              setCode(initial_code);

              setOutput("");
              setHightlightColor(() => hightlight);
            }}
            isResetDisabled={true}
          />
        )}
      </Box>
      {showExplanation && (
        <EditorExplanation
          explanation={currentExplanation}
          currentIdx={currentHighlightIndex}
          onPrevClick={handlePrevious}
          onPrevIsDisabled={!hasHighlights || currentHighlightIndex === 0}
          onNextClick={handleNext}
          onNextIsDisabled={
            !hasHighlights || currentHighlightIndex === explanation!.length - 1
          }
        />
      )}
    </Box>
  );
}

export default PythonCodeEditor;
